import { bundleComposition } from "./bundle"
import { isQaQcEligible } from "./addon"
import { SERVICE_CONFIGS } from "./services"

const SERVICE_FIELD_ALIASES = {
  cover_design_samples: "design_samples",
  color_palettes: "color_palettes",
  six: "style",
  trigger_warning_details: "triggerWarning",
}

const MANAGED_FIELDS = new Set([
  "managed_services_tier", "freelancer_note", "freelancer_selection", "pre_scheduling",
  "progress_updates", "zoom_call", "additional_services", "quality_waiver",
  "timeframe_waiver",
])

function visibleFieldTarget(service, fieldName, state, ctx, queue, getSteps, visibleFields) {
  const queueIndex = queue.indexOf(service)
  if (queueIndex < 0 || !fieldName) return null

  const values = state.postings[service] || {}
  const steps = getSteps(service, values, ctx)
  for (const [stepIndex, step] of steps.entries()) {
    const field = visibleFields(step, values, ctx).find((candidate) =>
      candidate.key === fieldName ||
      candidate.detailsKey === fieldName ||
      (fieldName === "triggerWarning" && candidate.type === "triggerWarning")
    )
    if (field) {
      return {
        stage: "service", queueIndex, stepIndex,
        focusKey: field.key || field.type,
        label: SERVICE_CONFIGS[service]?.name || service,
      }
    }
  }
  return null
}

// Targets are derived from the same visible fields the wizard renders. The
// backend service name alone is insufficient: some postings are derived and
// cannot be opened from the customer's configurable queue.
export function resolveIssueTarget(issue, state, ctx, queue, getSteps, visibleFields, hasAddon) {
  const service = issue?.service
  const field = issue?.field
  if (!service || !field) return null

  if (service === "managed_services" || service === "quality_assurance") {
    if (!hasAddon) return null
    if (service === "managed_services") {
      if (!MANAGED_FIELDS.has(field)) return null
      if (field !== "managed_services_tier" && state.managed_services_tier !== "managed") return null
    } else if (field !== "quality_assurance_addon" || !isQaQcEligible(ctx.selected)) return null

    return { stage: "addon", focusKey: field, label: "Management & review" }
  }

  if (state.bundle) {
    const composition = bundleComposition(state.bundleTier)
    const bundleFields = {
      bundle_cover_file: "bundle_cover_file",
      bundle_description_file: composition.description && !state.postings.book_writing?.use_ai
        ? "bundle_description_file" : null,
      bundle_narration_file: composition.narration ? "bundle_narration_file" : null,
    }
    if (service === "bundle" && bundleFields[field]) {
      return { stage: "hub", focusKey: bundleFields[field], label: "Bundle materials" }
    }
    if (service === "narration") {
      if (field === "character_voice_count" && composition.narration && state.postings.book_writing?.fiction) {
        return { stage: "hub", focusKey: field, label: "Bundle materials" }
      }
      if (field === "word_count") {
        return visibleFieldTarget("book_writing", field, state, ctx, queue, getSteps, visibleFields)
      }
      return null
    }
    if (service === "outline") {
      if (field === "collaborative" && composition.outlineChild) {
        return { stage: "hub", focusKey: field, label: "Bundle materials" }
      }
      if (["title", "word_count", "writing_style", "genre", "niche", "nsfw_details", "trigger_warning_details"].includes(field)) {
        return visibleFieldTarget("book_writing", SERVICE_FIELD_ALIASES[field] || field, state, ctx, queue, getSteps, visibleFields)
      }
      return null
    }
    if (service === "cover_design" && ["title", "subtitle", "author_name", "niche"].includes(field)) {
      return visibleFieldTarget("book_writing", field, state, ctx, queue, getSteps, visibleFields)
    }
    if (["formatting", "book_description"].includes(service) && ["title", "word_count", "niche"].includes(field)) {
      return visibleFieldTarget("book_writing", field, state, ctx, queue, getSteps, visibleFields)
    }
  } else if (service === "outline" && !state.selected.includes("outline")) {
    // BYO Book Writing's add-an-outline is a derived child. A standalone
    // Outline selected in the saved cursor remains customer-owned and editable.
    if (!state.selected.includes("book_writing")) return null
    const ownerField = field === "package_type" ? "add_outline" : field
    return visibleFieldTarget("book_writing", SERVICE_FIELD_ALIASES[ownerField] || ownerField,
      state, ctx, queue, getSteps, visibleFields)
  }

  return visibleFieldTarget(service, SERVICE_FIELD_ALIASES[field] || field,
    state, ctx, queue, getSteps, visibleFields)
}
